  import React, { useState,
    useEffect,
    useMemo,
    useCallback,
    useRef, } from "react";
  import axios from 'axios';
  import dayjs from 'dayjs';
  import {
    Container,
    Paper,
    TextField,
    Autocomplete,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    Card,
    CardContent,
    Snackbar,
    Alert,
    Box,
    ThemeProvider,
    createTheme,
    Checkbox,
    Chip,
    FormControl,
    Select,
    MenuItem,
    Tooltip,
    ButtonGroup,
    Divider,
    Menu,
    InputAdornment,
    Tabs,
    Tab,
    TableSortLabel,
    CircularProgress,
  } from "@mui/material";
  import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
  import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
  import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
  import SearchIcon from "@mui/icons-material/Search";
  import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
  import MoreVertIcon from "@mui/icons-material/MoreVert";
  import AddIcon from "@mui/icons-material/Add";
  import CloseIcon from "@mui/icons-material/Close";
  import { styled } from "@mui/material/styles";
  import RefreshIcon from "@mui/icons-material/Refresh";
  import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
  import BusinessIcon from '@mui/icons-material/Business';
  import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
  import DealDeskIcon from '@mui/icons-material/Handshake';
  import DealDesk from './DealDesk'; 
 

  const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN; 

  const suggestionsCache = new Map();
  let debounceTimer;

  const fetchSuggestions = async (query, setter) => {
    const trimmed = query?.trim().toLowerCase();
    if (!trimmed || trimmed.length < 3) {
      setter([]);
      return;
    }
    
    if (suggestionsCache.has(trimmed)) {
      setter(suggestionsCache.get(trimmed));
      return;
    }

    const suggestions = [];
    
    // If it's a 2-letter input, add it as state code option
    if (trimmed.length === 2 && /^[A-Z]{2}$/i.test(trimmed)) {
      suggestions.push(trimmed.toUpperCase());
    }
    
    if (debounceTimer) clearTimeout(debounceTimer);

    debounceTimer = setTimeout(async () => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&types=place,region&limit=5&country=us&language=en`
      );
      const data = await response.json();

      const places = data.features.map((f) => {
        const isState = f.place_type.includes('region');
        
        if (isState) {
          const stateCode = f.properties?.short_code?.split('-')[1]?.toUpperCase() || 
                          f.context?.find(c => c.id.startsWith('region'))?.short_code?.split('-')[1]?.toUpperCase() ||
                          f.text?.toUpperCase();
          return stateCode;
        }
        
        const city = f.text; 
        let state = "";
        const stateObj = f.context?.find((c) => c.id.startsWith("region"));
        if (stateObj && stateObj.short_code) {
          state = stateObj.short_code.split("-")[1].toUpperCase(); 
        }

        return state ? `${city}, ${state}` : city;
      });

        const finalResults = [...suggestions, ...places];
        suggestionsCache.set(trimmed, finalResults);
        setter(finalResults);
    } catch (error) {
      console.error("Mapbox Error:", error);
      setter(suggestions);
    }
    }, 300);
  };

  const getRegistrationType = (registrationNumber) => {
    if (!registrationNumber) return 'DOT/MC';
    const numStr = String(registrationNumber).toUpperCase();
    if (numStr.includes('MC-') || numStr.startsWith('MC')) {
      return 'MC';
    }
    return 'DOT';
  };

  const fetchMapboxSuggestions = async (query) => {
    const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    if (!token || !query) return [];
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`;
      const resp = await axios.get(url, {
        params: {
          access_token: token,
          limit: 6,
          country: 'us',
          types: 'place,region,address,postcode',
          autocomplete: false
        }
      });

      return (resp.data.features || []).map(f => {
        const isStateOnly = f.place_type.includes('region');
        let place_name = f.place_name;
        let state_code = '';

        if (isStateOnly) {
          if (f.properties && f.properties.short_code) {
            state_code = f.properties.short_code.split('-').pop();
          } else {
            const stateObj = (f.context || []).find(c => c.id && c.id.startsWith('region'));
            if (stateObj && stateObj.short_code) {
              state_code = stateObj.short_code.split('-').pop();
            } else {
              state_code = f.text; 
            }
          }
          place_name = state_code;
        } else {
          const city = f.text || '';
          const stateObj = (f.context || []).find(c => c.id && c.id.startsWith('region'));
          if (stateObj && stateObj.short_code) {
            state_code = stateObj.short_code.split('-').pop();
            place_name = `${city}, ${state_code}`;
          }
        }

        return {
          id: f.id,
          place_name: place_name,
          center: isStateOnly ? null : f.center,
          isStateOnly
        };
      });
    } catch (err) {
      console.error('mapbox suggestions error', err);
      return [];
    }
  };

  const normalizePlaceName = (place) => {
    if (!place) return '';
    try {
      const p = String(place).trim();
      const replaced = p.replace(/\bUS-([A-Z]{2})\b/ig, (m, g1) => g1.toUpperCase());
      if (replaced.includes(',')) {
        const parts = replaced.split(',').map(s => s.trim());
        if (parts.length >= 2) {
          const second = parts[1].split(' ')[0];
          const state = second || '';
          return `${parts[0]}, ${state}`;
        }
      }
      return replaced;
    } catch (e) {
      return place;
    }
  };

  const columnWidths = {
    "Since": '5%',
    "DTO": '5%',
    "Origin": '11%',
    "Destination": '11%',
    "DFD": '5%',
    "Pickup": '5%',
    "Equip": '7%',
    "Feet": '5%',
    "Weight": '7%',
    "Capacity": '5%',
    "Company": '19%',
    "Contact": '11%',
    "Base": '4%'
  };

  const theme = createTheme({
    palette: {
      primary: { main: "#11224d" },
      secondary: { main: "#E1AD01" },
      background: { default: "#f3f4f6", paper: "#fff" },
    },
    typography: { fontSize: 12 },
  });

  const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
  }));

  const StyledCard = styled(Card)(({ theme }) => ({
    marginTop: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  }));

  const TableProgressBar = styled('div')(({ theme }) => ({
    width: '100%',
    height: '4px',
    backgroundColor: '#ffb300',
    overflow: 'hidden',
    position: 'relative',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-30%',
      width: '30%',
      height: '100%',
      background: `
        linear-gradient(
          90deg,
          transparent,
          rgba(255,255,255,0.6),
          rgba(255,255,255,0.9),
          rgba(255,255,255,0.6),
          transparent
        )
      `,
      animation: 'windowsShimmer 3s linear infinite',
    },

    '@keyframes windowsShimmer': {
      '0%': { left: '-30%' },
      '100%': { left: '100%' },
    },
  }));

    const CustomSortArrow = (props) => (
      <KeyboardArrowUpIcon
        {...props}
        sx={{
          color: '#E1AD01 !important', 
          fontSize: '0.6rem',
        }}
      />
    );

  const formatRelativeTime = (timePosted) => {
    const diffInSec = dayjs().diff(dayjs(timePosted), 'second');
    if (diffInSec < 60) return `${diffInSec} s`;
    const diffInMin = dayjs().diff(dayjs(timePosted), 'minute');
    if (diffInMin < 60) return `${diffInMin} m`;
    const diffInHr = dayjs().diff(dayjs(timePosted), 'hour');
    if (diffInHr < 24) return `${diffInHr} h`;
    const diffInDays = dayjs().diff(dayjs(timePosted), 'day');
    return `${diffInDays} d`;
  };

const FMCSAButton = ({ truck }) => {
  // Only show for non-admin trucks
  if (truck.admin_id || truck.posted_by_role === 'admin') return null;
  
  // ✅ Use DOT only (not MC)
  const dotNumber = truck.dot_number;
  if (!dotNumber) return null;

  const handleFMCSACheck = (e) => {
    e.stopPropagation();
    
    // Clean any accidental spaces or non-digits out of the DOT number string
    const numOnly = String(dotNumber).replace(/\D/g, '');
    if (numOnly) {
      window.open(`https://motus.dot.gov/customer/${numOnly}/account`, '_blank');
    } else {
      window.open(`https://motus.dot.gov`, '_blank');
    }
  };

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<SearchIcon sx={{ fontSize: '1rem' }} />}
      onClick={handleFMCSACheck}
      sx={{
        borderColor: '#4dabf5',
        color: '#4dabf5',
        fontWeight: 600,
        mt: 1,
        width: { xs: '100%', sm: 'auto' },
        '&:hover': {
          borderColor: '#1e88e5',
          backgroundColor: 'rgba(77,171,245,0.08)',
          color: '#1e88e5',
        },
      }}
    >
      Verify FMCSA Record
    </Button>
  );
};
  function Row({ truck, destinationType, equipmentTypes, onOpenDealDesk }) {
    const [open, setOpen] = useState(false);

    const formatPhoneNumber = (phoneNumber) => {
      //

      if (!phoneNumber) {
        return ''; 
      }
    
      let cleaned = ('' + phoneNumber).replace(/\D/g, '');
      if (cleaned.length === 11 && cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1); 
      }
    
      if (cleaned.length === 10) {
        const areaCode = cleaned.substring(0, 3);
        const middle = cleaned.substring(3, 6);
        const last = cleaned.substring(6, 10);
    
        return `(${areaCode}) ${middle}-${last}`;
      }
      return phoneNumber; 
    };

    

    const rawContact = truck.contactInfo || truck.carrierPhone || truck.carrierEmail || 'N/A';
    const DetailSection = ({ title, children }) => (
              <Box sx={{ pr: 2, mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        color: "#11224d",
                        mb: 0.5,
                        borderBottom: '1px solid #ddd',
                        pb: 0.5,
                      }}
                    >
                      {title}
                    </Typography>
                    {children}
                  </Box>
            );
      
    return (
      <>
        <TableRow
    hover
    onClick={() => setOpen(!open)}
    sx={{
      cursor: "pointer",
      "& td": {
        position: "relative",
        textAlign: "center", 
        verticalAlign: "middle", 
      },
      "& td:not(:last-child)::after": {
        content: '""',
        position: "absolute",
        right: 0,
        top: "25%",
        height: "50%",
        borderRight: "1px solid #ddd",
      },
    }}
  >
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {formatRelativeTime(truck.timePosted)}
    </TableCell>
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {(truck.deadheadOrigin ?? truck.deadhead_origin_miles) != null
        ? `${Number(truck.deadheadOrigin ?? truck.deadhead_origin_miles).toFixed(0)}`
        : "-"}
    </TableCell >
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {normalizePlaceName(truck.origin) || "N/A"}
    </TableCell>
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {normalizePlaceName(truck.destination) || "N/A"}
    </TableCell>
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {destinationType === 'cityState' && (truck.deadheadDestination ?? truck.deadhead_destination_miles) != null
        ? `${Number(truck.deadheadDestination ?? truck.deadhead_destination_miles).toFixed(0)} `
        : "-"}
    </TableCell>
    <TableCell>
      {truck.availableDate
      ? dayjs(truck.availableDate).format('MM/DD')
      : "-"}
    </TableCell >
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{(truck.truckType || '').split(',').map(e => equipmentTypes.find(et => et.label === e.trim())?.short || e).join(', ')}</TableCell>
    <TableCell>{truck.length ? `${truck.length} ft` : "-"}</TableCell>
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{truck.weight ? `${truck.weight} lbs` : "-"}</TableCell>
    <TableCell>{truck.fullOrPartial ?? "-"}</TableCell>
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" , fontWeight: "bold",
      color: "#1976d2",}}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {truck.admin_verification_status === 'verified' ? (
          <Tooltip title="Verified Carrier">
            <VerifiedUserIcon sx={{ color: '#2196F3', fontSize: '1rem', mr: 0.5 }} />
          </Tooltip>
        ) : (
            <Tooltip title="Unverified Carrier">
              <VerifiedUserIcon sx={{ color: 'grey', fontSize: '1rem', mr: 0.5 }} />
            </Tooltip>
        )}
        {truck.companyName}
      </Box>
    </TableCell>
    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: "bold",
      color: "#1976d2", }}>
      {(() => {
        if (!rawContact || rawContact === 'N/A') return <span style={{ color: 'rgba(0, 0, 0, 0.87)', fontWeight: 'normal' }}>-</span>;
        
        const isEmail = rawContact.includes('@');
        const href = isEmail ? `mailto:${rawContact}` : `tel:${rawContact.replace(/\D/g, '')}`;
        const display = isEmail ? rawContact : formatPhoneNumber(rawContact);

        return (
          <Tooltip title={String(rawContact)}>
            <a href={href} onClick={(e) => e.stopPropagation()} style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
              <span style={{ display: 'inline-block', maxWidth: '100%' }}>{display}</span>
            </a>
          </Tooltip>
        );
      })()}
    </TableCell>
    <TableCell sx={{ fontWeight: "bold",color: "#1976d2", }}>{truck.companyState}</TableCell>
  </TableRow>
        <TableRow>
  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
    <Collapse in={open} timeout="auto" unmountOnExit>
      <StyledCard>
        <CardContent>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              mb: 1,
              fontSize: "1rem",
              color: "#11224d",
            }}
          >
            {normalizePlaceName(truck.origin)} → {normalizePlaceName(truck.destination)} — ({truck.companyState})
          </Typography>
          <Divider />

          {/* 
            DESKTOP: flex row, three equal columns (original)
            MOBILE:  flex column, sections stack full-width
          */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              width: "100%",
              mt: 1,
              gap: { xs: 2, sm: 0 },
            }}
          >

            {/* ── Truck Details ── */}
            <Box sx={{ flex: 1, pr: { xs: 0, sm: 2 } }}>
              <DetailSection title="Truck Details">
                <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
                    {/* Route line indicator */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mr: 1.5,
                        mt: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          border: "2px solid #1976d2",
                          backgroundColor: "white",
                        }}
                      />
                      <Box
                        sx={{
                          width: 0,
                          height: 32,
                          borderLeft: "1px dashed #bdbdbd",
                        }}
                      />
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#1976d2",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {truck.origin}{" "}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {truck.availableDate ? dayjs(truck.availableDate).format('MM/DD/YYYY') : "N/A"}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold", mt: 1, mb: 1.5 }}>
                        {truck.destination}{" "}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography variant="body2"><strong>Truck ID: </strong> TRK-{truck.id}</Typography>
                <Typography variant="body2"><strong>Equipment:</strong> {(truck.truckType || '').split(',').join(', ')}</Typography>
                <Typography variant="body2"><strong>Available Date:</strong> {truck.availableDate ? dayjs(truck.availableDate).format('MM/DD/YYYY') : '-'}</Typography>
                <Typography variant="body2"><strong>Load:</strong> {truck.fullOrPartial}</Typography>
                <Typography variant="body2"><strong>Length:</strong> {truck.length ? `${truck.length} ft` : '-'}</Typography>
                <Typography variant="body2"><strong>Weight:</strong> {truck.weight ? `${truck.weight} lbs` : '-'}</Typography>
              </DetailSection>
            </Box>

            {/* ── Company ── */}
            <Box sx={{ flex: 1 }}>
              <DetailSection title="Company">
                <Typography variant="body2">
                  <span style={{ fontWeight: "bold" }}>Company:</span>{" "}
                  {truck.companyName}
                </Typography>
                <Typography variant="body2">
                  
{(truck.dot_number || truck.registration_number) && (
  <Typography variant="body2">
    <span style={{ fontWeight: "bold" }}>DOT Number:</span>{" "}
    {truck.dot_number}
  </Typography>
)}
{truck.mc_number && truck.mc_number !== truck.dot_number && (
  <Typography variant="body2">
    <span style={{ fontWeight: "bold" }}>MC Number:</span>{" "}
    {truck.mc_number.toString().replace(/^MC-?/gi, '').trim()}
  </Typography>
)}
                  
                </Typography>
                <Typography variant="body2">
                  <span style={{ fontWeight: "bold" }}>State:</span>{" "}
                  {truck.companyState}
                </Typography>
                <Typography variant="body2">
                  <strong>Contact:</strong>{" "}
                  {(() => {
                    if (!rawContact || rawContact === 'N/A') return 'N/A';
                    const isEmail = rawContact.includes('@');
                    const href = isEmail ? `mailto:${rawContact}` : `tel:${rawContact.replace(/\D/g, '')}`;
                    const display = isEmail ? rawContact : formatPhoneNumber(rawContact);
                    return (
                      <a
                        href={href}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {display}
                      </a>
                    );
                  })()}
                </Typography>
              </DetailSection>

              {/* Deal Desk Button — full width on mobile */}
              <Tooltip title={(truck.admin_id || truck.posted_by_role === 'admin') ? "Not Available" : ""}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={Boolean(truck.admin_id || truck.posted_by_role === 'admin')}
                    startIcon={<PermContactCalendarIcon sx={{ fontSize: '1rem' }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDealDesk(truck);
                    }}
                    sx={{
                      backgroundColor: '#ffb300',
                      color: '#fff',
                      fontWeight: 600,
                      mt: { xs: 1, sm: 0 },
                      width: { xs: '100%', sm: 'auto' },   // full-width on mobile
                      '&:hover': {
                        backgroundColor: '#ffb300',
                      },
                    }}
                  >
                    Contact in DealDesk
                  </Button>
                </span>
              </Tooltip>
              <FMCSAButton truck={truck} />
            </Box>

            {/* ── Notes ── */}
            <Box sx={{ flex: 1, pr: { xs: 0, sm: 2 } }}>
              <DetailSection title="Notes">
                <Typography variant="body2"><strong>Notes:</strong> {truck.comments || '-'}</Typography>
              </DetailSection>
            </Box>
    </Box>
          
        </CardContent>
      
      </StyledCard>
      
    </Collapse>
  </TableCell>
        </TableRow>
      </>
    );
  }

  const initialFormData = {
    truckType: [],
    origin: "",
    destination: "",
    startDate: new Date(), 
    endDate: new Date(),        
    deadheadOrigin: 150,
    deadheadDestination: 150,
    fullOrPartial: "Full or Partial",
    length: "",
    weight: "",
    searchAge: "4",
  };
  const MAX_TABS = 5;

  const SortSelectOptions = [
      { label: "Since - Newest", key: 'Age', order: 'asc' },
      { label: "Pickup - Soonest", key: 'Pickup', order: 'asc' },
      { label: "Pickup - Latest", key: 'Pickup', order: 'desc' },
      { label: "Company Name - A to Z", key: 'Company', order: 'asc' },
      { label: "Company Name - Z to A", key: 'Company', order: 'desc' },
      { label: "Weight - Lightest", key: 'Weight', order: 'asc' },
      { label: "Weight - Heaviest", key: 'Weight', order: 'desc' },
      { label: "Feet - Shortest", key: 'Feet', order: 'asc' },
      { label: "Feet - Longest", key: 'Feet', order: 'desc' },
      { label: "Deadhead to Origin - Shortest", key: 'DH-O', order: 'asc' }, 
      { label: "Deadhead from Destination - Shortest", key: 'DH-D', order: 'asc' },
  ];

  const headCells = [
    { id: 'Since', label: 'Since', sortable: true },
    { id: 'DH-O', label: 'DTO', sortable: true, tooltip: 'Deadhead to Origin' },
      { id: 'Origin', label: 'Origin', sortable: false },
      { id: 'Destination', label: 'Destination', sortable: false },
      { id: 'DH-D', label: 'DFD', sortable: true, tooltip: 'Deadhead from Destination' },
    { id: 'Pickup', label: 'Pickup', sortable: true },
    { id: 'Equip', label: 'Equip', sortable: false },
    { id: 'Feet', label: 'Feet', sortable: true },
    { id: 'Weight', label: 'Weight', sortable: true },
    { id: 'Capacity', label: 'Capacity', sortable: false },
    { id: 'Company', label: 'Company', sortable: true },
    { id: 'Contact', label: 'Contact', sortable: false },
    { id: 'Base', label: 'Base', sortable: false },
  ];
  function SearchTrucks({ onOpenDealDesk, onSearchResultsChange }) {
    const getStorageKey = () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          return `searchTrucksTabs_${user.id}`;
        } catch (e) {
          console.error("Error parsing user from local storage", e);
        }
      }
      return 'searchTrucksTabs_guest';
    };

    const [activeTab, setActiveTab] = useState(() => {
      const key = getStorageKey();
      const saved = localStorage.getItem(`${key}_activeTab`);
      return saved !== null ? Number(saved) : 0;
    });
    const [formOpen, setFormOpen] = useState(true);

    const [tabs, setTabs] = useState(() => {
      const key = getStorageKey();
      const saved = localStorage.getItem(`${key}_tabs`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return parsed.map(t => ({
            ...t,
            formData: {
              ...t.formData,
              startDate: t.formData.startDate ? new Date(t.formData.startDate) : null,
              endDate: t.formData.endDate ? new Date(t.formData.endDate) : null,
            }
          }));
        } catch (e) {
          console.error("Failed to load tabs", e);
        }
      }
      return [{
        id: 0, name: "Search 1", formData: { ...initialFormData }, searchResults: [],
        page: 1, hasMore: true, loadingMore: false, snackbarOpen: false, searchedOrigin: null,
      }];
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const [originOptionsState, setOriginOptionsState] = useState([]);
    const [destinationOptionsState, setDestinationOptionsState] = useState([]);

  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);

  const [equipmentTypes, setEquipmentTypes] = useState([]); 
    const [sortConfig, setSortConfig] = useState(SortSelectOptions[0]);

    const [userRole, setUserRole] = useState(null);

    const [tick, setTick] = useState(0);
    const [isDataOutdated, setIsDataOutdated] = useState(false);
    const [lastSearchedFormData, setLastSearchedFormData] = useState(null);
    // Note: Local dealDeskOpen state removed to use Dashboard's global instance.

    useEffect(() => {
      const key = getStorageKey();
      localStorage.setItem(`${key}_tabs`, JSON.stringify(tabs));
      localStorage.setItem(`${key}_activeTab`, activeTab);
    }, [tabs, activeTab]);

    useEffect(() => {
      if (tabs.length > 0 && activeTab >= tabs.length) {
        setActiveTab(tabs.length - 1);
      }
    }, [tabs.length, activeTab]);

    useEffect(() => {
      const role = localStorage.getItem('role');
      setUserRole(role);
    }, []);

    const currentTab = tabs[activeTab] || tabs[0] || {
      formData: initialFormData,
      searchResults: [],
      page: 1,
      hasMore: true,
      loadingMore: false,
      snackbarOpen: false,
      searchedOrigin: null,
    };
    const { formData, searchResults, snackbarOpen, page, hasMore, loadingMore } = currentTab;
      useEffect(() => {
          const getEquipment = async () => {
              try {
                  const token = localStorage.getItem('jwtToken');
                  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                  const { data } = await axios.get(`${API_BASE}/api/public/equipment`, { 
                      headers: { Authorization: `Bearer ${token}` } 
                  });
                  setEquipmentTypes(data);
              } catch (error) {
                  console.error("Could not load equipment types for dropdown.", error);
              }
          };
          getEquipment();
      }, []);
  const isFalsyOrEmptyString = (value) => {
    if (!value) return true;
    
    if (typeof value === 'string') {
      return value.trim().length === 0;
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return false;
  };

  const originText =
    typeof formData.origin === "string"
      ? formData.origin.trim()
      : (formData.origin?.label || formData.origin?.value || formData.origin?.place_name || "").trim();

  const destinationText =
    typeof formData.destination === "string"
      ? formData.destination.trim()
      : (formData.destination?.label || formData.destination?.value || formData.destination?.place_name || "").trim();
      


  const isLocationValid = (loc) => {
    if (!loc) return true;
    const locStr = typeof loc === 'string' ? loc : (loc.place_name || '');
    
    // Check if it's a 2-letter state code (case-insensitive)
    if (locStr.length === 2 && /^[A-Za-z]{2}$/i.test(locStr)) return true;
    
    // Check if it's "City, ST" format (case-insensitive for state)
    const parts = locStr.split(',');
    if (parts.length < 2) return false;
    const state = parts[1].trim();
    return state.length === 2 && /^[A-Za-z]{2}$/i.test(state);
  };



  const isOriginInvalid = originText && !isLocationValid(originText);
  const isDestinationInvalid = destinationText && !isLocationValid(destinationText);

  const isSearchDisabled = (
    (isFalsyOrEmptyString(formData.origin) && isFalsyOrEmptyString(formData.destination)) ||
    formData.truckType.length === 0 || 
    !formData.fullOrPartial ||
    !formData.startDate ||
    isOriginInvalid ||
    isDestinationInvalid
  );
    const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
    };

    const handleAddTab = () => {
      if (tabs.length >= MAX_TABS) return;
      const newTabId = tabs.length > 0 ? Math.max(...tabs.map((t) => t.id)) + 1 : 0;
      const newTab = {
        id: newTabId,
        name: `Search ${newTabId + 1}`,
        formData: { ...initialFormData },
        searchResults: [],
        page: 1,
        hasMore: true,
        loadingMore: false,
        snackbarOpen: false,
        searchedOrigin: null,
      };
      setTabs((prevTabs) => [...prevTabs, newTab]);
      setActiveTab(tabs.length);
    };

  const handleRemoveTab = (tabIdToRemove, event) => {
      event.stopPropagation();
      
      const tabIndexToRemove = tabs.findIndex(tab => tab.id === tabIdToRemove);
      
      // If it's the last tab, reset it to default instead of removing
      if (tabs.length === 1) {
        setTabs([{
          id: 0,
          name: "Search 1",
          formData: { ...initialFormData },
          searchResults: [],
          page: 1,
          hasMore: true,
          loadingMore: false,
          snackbarOpen: false,
          searchedOrigin: null,
        }]);
        setLastSearchedFormData(null); // Clear last searched data
        setActiveTab(0);
        return;
      }
      
      setTabs((prevTabs) => {
        const filteredTabs = prevTabs.filter((tab) => tab.id !== tabIdToRemove);
        return filteredTabs;
      });
      
      if (activeTab >= tabIndexToRemove && activeTab > 0) {
        setActiveTab(activeTab - 1);
      } else if (activeTab >= tabs.length - 1) {
        setActiveTab(tabs.length - 2);
      }
    };

    const updateCurrentTab = (updates) => {
      setTabs((prevTabs) =>
        prevTabs.map((tab, index) =>
          index === activeTab ? { ...tab, ...updates } : tab
        )
      );
    };

    const setFormData = (newFormData) => {
      updateCurrentTab({ formData: newFormData });
    };

    const setSearchResults = (newSearchResults) => {
      updateCurrentTab({ searchResults: newSearchResults });
    };

    const setSnackbarOpen = (isOpen) => {
      updateCurrentTab({ snackbarOpen: isOpen });
    };

    const handleClear = () => {
    updateCurrentTab({
      formData: { 
        ...initialFormData, 
        fullOrPartial: "Full or Partial",
        startDate: new Date(),
        endDate: null
      },
      searchResults: [],
      page: 1,
      hasMore: true,
    });
    setSearchResults([]);
  };

  const searchedOriginName = formData.origin || null;

  const handleSearch = useCallback(async (isBackground = false) => {
    if (!formData) return; 

    // For background refresh, use last searched data
    const searchFormData = isBackground && lastSearchedFormData ? lastSearchedFormData : formData;

    // CAPITALIZE states before validation and search
    const capitalizedOrigin = formatStateInput(searchFormData.origin || '');
    const capitalizedDestination = formatStateInput(searchFormData.destination || '');
    
    // Update formData with capitalized values ONLY if not a background search
    if (!isBackground && (capitalizedOrigin !== formData.origin || capitalizedDestination !== formData.destination)) {
      setFormData({ 
        ...formData, 
        origin: capitalizedOrigin, 
        destination: capitalizedDestination 
      });
    }

    // VALIDATE: Don't search if required fields are empty/invalid
    const originValue = capitalizedOrigin.trim();
    const destinationValue = capitalizedDestination.trim();
    
    // Check if both origin and destination are empty
    if (!originValue && !destinationValue) {
      console.log('No search location provided');
      if (!isBackground) {
        updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
      }
      return;
    }
    
    // Check if equipment type is empty
    if (!searchFormData.truckType || searchFormData.truckType.length === 0) {
      console.log('No equipment type selected');
      if (!isBackground) {
        updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
      }
      return;
    }
    
    // Check if fullOrPartial is missing
    if (!searchFormData.fullOrPartial) {
      console.log('No load type selected');
      if (!isBackground) {
        updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
      }
      return;
    }
    
    // Check if start date is missing
    if (!searchFormData.startDate) {
      console.log('No start date provided');
      if (!isBackground) {
        updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
      }
      return;
    }
    
    // Validate location formats (case-insensitive check, already capitalized)
    const isLocationValid = (loc) => {
      if (!loc) return true;
      const isStateCode = loc.length === 2 && /^[A-Z]{2}$/.test(loc);
      const parts = loc.split(',');
      if (isStateCode) return true;
      if (parts.length < 2) return false;
      const state = parts[1].trim();
      return state.length === 2 && /^[A-Z]{2}$/.test(state);
    };
    
    if ((originValue && !isLocationValid(originValue)) || 
        (destinationValue && !isLocationValid(destinationValue))) {
      console.log('Invalid location format');
      if (!isBackground) {
        updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
      }
      return;
    }

    // If all validations pass, proceed with search
    if (isBackground) {
      updateCurrentTab({ page: 1 });
    } else {
      updateCurrentTab({
        page: 1,
        hasMore: true,
        loadingMore: true,
      });
      // Store the search criteria for future background refreshes
      setLastSearchedFormData({ ...searchFormData, origin: capitalizedOrigin, destination: capitalizedDestination });
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const params = {};

      if (capitalizedOrigin) params.origin = capitalizedOrigin;
      if (capitalizedDestination) params.destination = capitalizedDestination;

      if (searchFormData.deadheadOrigin) params.originDeadhead = searchFormData.deadheadOrigin;
      if (searchFormData.deadheadDestination) params.destinationDeadhead = searchFormData.deadheadDestination;
      if (searchFormData.truckType && searchFormData.truckType.length) {
        params.truckType = searchFormData.truckType.join(',');
      }
      if (searchFormData.fullOrPartial) params.fullOrPartial = searchFormData.fullOrPartial;
      if (searchFormData.startDate) {
        params.startDate = dayjs(searchFormData.startDate).format('YYYY-MM-DD');
      }
      if (searchFormData.endDate) {
        params.endDate = dayjs(searchFormData.endDate).format('YYYY-MM-DD');
      }
      if (searchFormData.length) params.exactLength = searchFormData.length;
      if (searchFormData.weight) params.exactWeight = searchFormData.weight;
      if (searchFormData.searchAge) params.searchAge = searchFormData.searchAge;

      const limit = 50;
      params.page = 1;
      params.limit = limit;
      const token = localStorage.getItem('jwtToken');
      const resp = await axios.get(`${API_BASE}/api/trucks/search`, { 
        params,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const fetchedResults = resp.data.results || [];

    updateCurrentTab({ 
    searchResults: fetchedResults, 
    hasMore: fetchedResults.length === limit, 
    loadingMore: false,
    // Only update tab name if this is NOT a background refresh
    ...(isBackground ? {} : {
      searchedOrigin: searchedOriginName,
      name: searchedOriginName 
        ? searchedOriginName 
        : `Search ${tabs[activeTab]?.id + 1}`
    })
  });
      onSearchResultsChange?.(fetchedResults);
      if (!isBackground) {
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Search trucks error', err);
      updateCurrentTab({ loadingMore: false });
      if (!isBackground) {
        setSnackbarOpen(true);
      }
    }
  }, [formData, lastSearchedFormData]);

  useEffect(() => {
    const channel = new BroadcastChannel('app_data_sync');
    channel.onmessage = (event) => {
      if (event.data === 'DATA_CHANGED') {
        setIsDataOutdated(true);
      }
    };
    return () => {
      channel.close();
    };
  }, []);

  useEffect(() => {
    const checkDataUpdate = () => {
      if (document.visibilityState === 'visible' && isDataOutdated) {
        handleSearch(true);
        setIsDataOutdated(false);
      }
    };

    window.addEventListener('focus', checkDataUpdate);
    document.addEventListener('visibilitychange', checkDataUpdate);
    window.addEventListener('pageshow', checkDataUpdate);

    return () => {
      window.removeEventListener('focus', checkDataUpdate);
      document.removeEventListener('visibilitychange', checkDataUpdate);
      window.removeEventListener('pageshow', checkDataUpdate);
    };
  }, [isDataOutdated, handleSearch]);

  useEffect(() => {
    const isCurrentTab =
      activeTab === tabs.findIndex(t => t.id === currentTab.id);

    if (!isCurrentTab || !searchResults?.length) return;
    
    // CHECK: Don't poll if search fields are invalid/empty
    const originValue = typeof formData.origin === 'string' ? formData.origin.trim() : '';
    const destinationValue = typeof formData.destination === 'string' ? formData.destination.trim() : '';
    // CHECK: Don't poll if search fields are invalid/empty
    const hasValidSearch = lastSearchedFormData && 
                          (lastSearchedFormData.origin?.trim() || lastSearchedFormData.destination?.trim()) && 
                          lastSearchedFormData.truckType?.length > 0 && 
                          lastSearchedFormData.fullOrPartial &&
                          lastSearchedFormData.startDate;
    
    if (!hasValidSearch) return; // Don't start polling if no valid search was performed

    let intervalId = null;
    let isPollingActive = false;

    const startPolling = () => {
      if (isPollingActive) return;
      isPollingActive = true;

      intervalId = setInterval(() => {
        if (!loadingMore) {
          handleSearch(true);
        }
      }, 5000);
    };

    const stopPolling = () => {
      if (!isPollingActive) return;
      isPollingActive = false;

      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const updatePollingState = () => {
      const shouldBeActive =
        document.visibilityState === 'visible' &&
        document.hasFocus();

      if (shouldBeActive) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    updatePollingState();

    document.addEventListener('visibilitychange', updatePollingState);
    window.addEventListener('focus', updatePollingState);
    window.addEventListener('blur', updatePollingState);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', updatePollingState);
      window.removeEventListener('focus', updatePollingState);
      window.removeEventListener('blur', updatePollingState);
    };
  }, [
    activeTab,
    searchResults?.length,
    loadingMore,
    tabs,
    currentTab.id,
    formData.origin,
    formData.destination,
    formData.truckType,
    formData.fullOrPartial,
    formData.startDate,
    lastSearchedFormData
  ]);


  const handleLoadMore = useCallback(async () => {
    if (!formData || loadingMore || !hasMore) return;

    updateCurrentTab({ loadingMore: true });

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const params = {};
      const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      if (formData.origin) params.origin = formData.origin;
      if (formData.destination) params.destination = formData.destination;
      if (formData.deadheadOrigin) params.originDeadhead = formData.deadheadOrigin;
      if (formData.deadheadDestination) params.destinationDeadhead = formData.deadheadDestination;
      if (formData.truckType && formData.truckType.length) {
        params.truckType = formData.truckType.join(',');
      }
      if (formData.fullOrPartial) params.fullOrPartial = formData.fullOrPartial;
  if (formData.startDate) {
    params.startDate = dayjs(formData.startDate).format('YYYY-MM-DD');
  }
  if (formData.endDate) {
    params.endDate = dayjs(formData.endDate).format('YYYY-MM-DD');
  }
  if (formData.length) params.exactLength = formData.length;
      if (formData.weight) params.exactWeight = formData.weight;
      if (formData.searchAge) params.searchAge = formData.searchAge;

      const limit = 50;
      params.page = page + 1;
      params.limit = limit;

      const resp = await axios.get(`${API_BASE}/api/trucks/search`, { 
        params,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const fetchedResults = resp.data.results || [];

      updateCurrentTab({
        searchResults: [...searchResults, ...fetchedResults],
        hasMore: fetchedResults.length === limit,
        page: page + 1,
        loadingMore: false,
        
      });
      onSearchResultsChange?.([...searchResults, ...fetchedResults]);
    } catch (err) {
      console.error('Load more trucks error', err);
      updateCurrentTab({ loadingMore: false });
    }
  }, [formData, page, hasMore, loadingMore, searchResults]);

  const observer = useRef();
  const lastTruckElementRef = useCallback(node => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasMore) {
              handleLoadMore();
          }
      });
      if (node) observer.current.observe(node);
  }, [loadingMore, hasMore, handleLoadMore]);
    const sortedResults = useMemo(() => {
      if (!searchResults) return [];
      let sortableItems = [...searchResults];
      if (sortConfig) {
        sortableItems.sort((a, b) => {
          let aValue, bValue;

          switch (sortConfig.key) {
            case "Age": 
              aValue = dayjs().diff(dayjs(a.timePosted), "second");
              bValue = dayjs().diff(dayjs(b.timePosted), "second");
              break;
            case "DH-O":
              aValue = a.deadheadOrigin ?? a.deadhead_origin_miles ?? Infinity;
              bValue = b.deadheadOrigin ?? b.deadhead_origin_miles ?? Infinity;
              break;
            case "Pickup":
              aValue = a.availableDate ? new Date(a.availableDate) : 0;
              bValue = b.availableDate ? new Date(b.availableDate) : 0;
              break;
            case "Company":
              aValue = a.companyName || "";
              bValue = b.companyName || "";
              break;
            case "Weight":
              aValue = a.weight ?? -1;
              bValue = b.weight ?? -1;
              break;
            case "Feet":
              aValue = a.length ?? -1;
              bValue = b.length ?? -1;
              break;
            case "DH-D":
              aValue = a.deadheadDestination ?? a.deadhead_destination_miles ?? Infinity;
              bValue = b.deadheadDestination ?? b.deadhead_destination_miles ?? Infinity;
              break;
            default: return 0;
          }

          if (aValue < bValue) return sortConfig.order === "asc" ? -1 : 1;
          if (aValue > bValue) return sortConfig.order === "asc" ? 1 : -1;
          return 0;
        });
      }
      return sortableItems;
    }, [searchResults, sortConfig]);

    const handleRequestSort = (property) => {
      const isAsc = sortConfig.key === property && sortConfig.order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setSortConfig({ key: property, order: newOrder });
    };
    /**
   * @param {string} input 
   * @returns {string} 
   */
  const formatStateInput = (input) => {
      if (!input) return "";
      
      const trimmed = input.trim();
      
      // If it's just 2 letters, treat as state code and uppercase it
      if (trimmed.length === 2 && /^[A-Za-z]{2}$/.test(trimmed)) {
          return trimmed.toUpperCase();
      }
      
      // Handle "City, State" format
      const lastCommaIndex = trimmed.lastIndexOf(',');
      
      if (lastCommaIndex !== -1) {
          const cityPart = trimmed.slice(0, lastCommaIndex);
          const statePart = trimmed.slice(lastCommaIndex + 1).trim();
          return cityPart + ', ' + statePart.toUpperCase();
      }
      
      return trimmed;
  };
    
  const handleLocationBlur = async (event, fieldName) => {
      let currentValue = event.target.value.trim();
      if (!currentValue) {
        setFormData({ ...formData, [fieldName]: '' });
        return;
      }

      // Format existing input (capitalizes state codes)
      currentValue = formatStateInput(currentValue);

      // Check if it's a valid 2-letter state code
      const isStateCode = currentValue.length === 2 && /^[A-Z]{2}$/.test(currentValue);
      
      if (isStateCode) {
          // Valid state code, just keep it
          setFormData({ ...formData, [fieldName]: currentValue });
          return;
      }

      // Validate "City, ST" format
      const isPotentiallyInvalid = () => {
          const parts = currentValue.split(',');
          if (parts.length < 2) return true;
          const state = parts[1].trim();
          return state.length !== 2 || !/^[A-Z]{2}$/.test(state);
      };

      if (isPotentiallyInvalid()) {
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              currentValue
            )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&types=place,region&limit=1&country=us&language=en`
          );
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            const feature = data.features[0];
            
            // Check if it's a state/region result
            const isStateResult = feature.place_type.includes('region');
            
            if (isStateResult) {
                // Extract state code
                const stateCode = feature.properties?.short_code?.split('-')[1]?.toUpperCase() || 
                                feature.context?.find(c => c.id.startsWith('region'))?.short_code?.split('-')[1]?.toUpperCase() ||
                                feature.text?.toUpperCase();
                currentValue = stateCode || currentValue.toUpperCase();
            } else {
                // It's a city result
                const city = feature.text;
                const stateObj = feature.context?.find((c) => c.id.startsWith("region"));
                const state = stateObj?.short_code?.split("-")[1].toUpperCase() || '';
                currentValue = state ? `${city}, ${state}` : city;
            }
          } else {
            // If no results found, clear the invalid input
            currentValue = '';
          }
        } catch (error) {
          console.error("Mapbox onBlur Error:", error);
          currentValue = '';
        }
      }
      setFormData({ ...formData, [fieldName]: currentValue });
  };
    const currentSortOptionLabel = useMemo(() => {
        const option = SortSelectOptions.find(opt => opt.key === sortConfig.key && opt.order === sortConfig.order) 
                    || SortSelectOptions.find(opt => opt.key === sortConfig.key); 
        return option ? option.label : "";
    }, [sortConfig]);

    const handleMenuClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    if (!formData) { 
      return null;
    }

    return (
      <ThemeProvider theme={theme}>
        <style>
          {`
            html, body, #root {
              margin: 0;
              padding: 0;
              height: 100%;
              overflow: hidden;
            }
          `}
        </style>
        <Box
          sx={{
            backgroundColor: "#f3f4f6",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Container
            maxWidth="xl"
            disableGutters
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              height: "100%",
              overflow: "hidden",
              pl: 1,
              pr: 1,
              mt: 1,
            }}
          >
            
            <StyledPaper elevation={1} sx={{ mt: 0, mb: 0, p: 0 }}>
          
              <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "#e0e0e0" }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 0,
                    "& .MuiTabs-indicator": { backgroundColor: theme.palette.primary.main, },
                    "& .MuiTab-root": {
                      fontSize: "0.75rem", py: 0.3, px: 1, textTransform: "none", fontWeight: "bold",
                      minHeight: 24, color: "#555", minWidth: 40,
                    },
                    "& .Mui-selected": {
                      color: theme.palette.primary.main, backgroundColor: "#fff",
                      borderRadius: "5px 5px 0 0",
                    },
                  }}
                >
                {tabs.map((tab, index) => (
    <Tab
      key={tab.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 20 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', color: '#4dabf5'  }}>
            {tab.searchedOrigin || tab.name}
          </Typography>
          <IconButton
            size="small"
            onClick={(event) => handleRemoveTab(tab.id, event)}
            sx={{ ml: 0.5, p: 0, '&:hover': { color: 'error.main' } }}
          >
            <CloseIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Box>
      }
      sx={{  
        minWidth: 'auto',
        maxWidth: 'none',
        width: 'auto',
      }}
    />
  ))}
            <Tab
                    key="add-new-tab"
                    disabled={tabs.length >= MAX_TABS}
                    label={
                      <IconButton
                        size="small"
                        onClick={handleAddTab}
                        sx={{ 
                          p: 0.5, 
                          color: theme.palette.primary.main,
                          opacity: tabs.length >= MAX_TABS ? 0.4 : 1,
                          cursor: tabs.length >= MAX_TABS ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <AddIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    }
                    sx={{ minWidth: 48, p: 0 }}
                  />
                </Tabs>
              </Box>

              {!formOpen && (
                  <Box
                      sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          px: 2,
                          py: 1
                      }}
                      onClick={() => setFormOpen(true)}
                  >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#11224d' }}>
                          Search Trucks
                      </Typography>
                      <Tooltip title="Expand Form">
                          <IconButton size="small">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                                  <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                                  <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5 }} />
                              </Box>
                          </IconButton>
                      </Tooltip>
                  </Box>
              )}
            
              <Collapse in={formOpen}>
                <Box sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: 1.2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, alignItems: 'center', flex: 1, width: '100%' }}>
                  
  <Autocomplete
    freeSolo
    size="small"
    options={originSuggestions}
    value={formData.origin}
    onInputChange={(event, newInputValue) => {
      // Don't format while typing - just store the raw value
      setFormData({ ...formData, origin: newInputValue });
                if (newInputValue) fetchSuggestions(newInputValue, setOriginSuggestions);
    }}
    onChange={(event, newValue) =>
      setFormData({ ...formData, origin: newValue || '' })
    }
    renderInput={(params) => {
      const originValue = typeof formData.origin === 'string' ? formData.origin.trim() : '';
      const isStateCode = originValue.length === 2 && /^[A-Za-z]{2}$/i.test(originValue);
      const isCityState = originValue.includes(',') && originValue.split(',')[1].trim().length === 2;
      const isInvalid = originValue && !isStateCode && !isCityState;
      
      return (
        <TextField
          {...params}
          label="Origin"
          error={isInvalid}
          helperText={isInvalid ? "Format: City, ST or ST" : ""}
          sx={{ flex: "1 1 150px", background: "#fff" }}
          onBlur={(e) => handleLocationBlur(e, 'origin')}
        />
      );
    }}
    sx={{ flex: "1 1 150px" }}
  />

  <TextField 
    size="small"
    label="DTO"
    variant="outlined"
    type="number"
    value={formData.deadheadOrigin}
    onChange={(e) => {
      let value = e.target.value;
      if (value === '') {
        setFormData({ ...formData, deadheadOrigin: '' });
        return;
      }
      if (value.length > 3) {
        value = value.slice(0, 3);
      }
      let numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 0) numValue = 0;
      if (numValue > 500) numValue = 500;
      setFormData({ ...formData, deadheadOrigin: numValue });
    }}
    inputProps={{ min: 0, max: 500, step: 1 }}
    InputProps={{
      endAdornment: <InputAdornment position="end">mi</InputAdornment>,
    }}
    sx={{ flex: "0 0 120px", background: "#fff" }}
  />

  <Autocomplete
    freeSolo
    size="small"
    options={destinationSuggestions}
    value={formData.destination}
    onInputChange={(event, newInputValue) => {
      // Don't format while typing - just store the raw value
      setFormData({ ...formData, destination: newInputValue });
                if (newInputValue) fetchSuggestions(newInputValue, setDestinationSuggestions);
    }}
    onChange={(event, newValue) =>
      setFormData({ ...formData, destination: newValue || '' })
    }
    renderInput={(params) => {
      const destValue = typeof formData.destination === 'string' ? formData.destination.trim() : '';
      const isStateCode = destValue.length === 2 && /^[A-Za-z]{2}$/i.test(destValue);
      const isCityState = destValue.includes(',') && destValue.split(',')[1].trim().length === 2;
      const isInvalid = destValue && !isStateCode && !isCityState;
      
      return (
        <TextField
          {...params}
          label="Destination"
          error={isInvalid}
          helperText={isInvalid ? "Format: City, ST or ST" : ""}
          sx={{ flex: "1 1 150px", background: "#fff" }}
          onBlur={(e) => handleLocationBlur(e, 'destination')}
        />
      );
    }}
    sx={{ flex: "1 1 150px" }}
  />
  <TextField
                
    size="small"
    label="DFD"
    variant="outlined"
    type="number"
    value={formData.deadheadDestination}
    onChange={(e) => {
      let value = e.target.value;
      if (value === '') {
        setFormData({ ...formData, deadheadDestination: '' });
        return;
      }
      if (value.length > 3) {
        value = value.slice(0, 3);
      }
      let numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 0) numValue = 0;
      if (numValue > 500) numValue = 500;
      setFormData({ ...formData, deadheadDestination: numValue });
    }}
    inputProps={{ min: 0, max: 500, step: 1 }}
    InputProps={{
      endAdornment: <InputAdornment position="end">mi</InputAdornment>,
    }}
    sx={{ flex: "0 0 120px", background: "#fff" }}
  />
  <Autocomplete

    multiple
    limitTags={2}
    disableCloseOnSelect
    size="small"
    id="equipment-type-select"
    sx={{
      flex: "1 1 150px",
      background: "#fff",
      "& .MuiAutocomplete-inputRoot": {
        flexWrap: "nowrap !important",
        overflowX: "auto",
        scrollbarWidth: "none", 
        "&::-webkit-scrollbar": { display: "none" }, 
      },
      "& .MuiChip-root": {
        maxHeight: 24,
        fontSize: 12,
      },
    }}
    options={equipmentTypes}
    getOptionLabel={(option) => option.label}
    value={equipmentTypes.filter((type) =>
      formData.truckType.includes(type.label)
    )}
    onChange={(event, newValue) => {
      const selectedLabels = newValue.map((option) => option.label);
      setFormData({ ...formData, truckType: selectedLabels });
    }}
    renderOption={(props, option, { selected }) => (
      <li {...props}>
        <Checkbox
          checked={selected}
          size="small"
          sx={{ mr: 1 }}
        />
        {`${option.label} (${option.short})`}
      </li>
    )}
    renderTags={(value, getTagProps) =>
      value.map((option, index) => (
        <Chip
          variant="outlined"
          label={option.short}
          size="small"
          {...getTagProps({ index })}
        />
      ))
    }
    renderInput={(params) => (
      <TextField
      required 
        {...params}
        label="Equipment Type"
        placeholder={formData.truckType.length > 0 ? "" : "Any"}
      />
    )}
  />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Box sx={{ display: "flex", gap: 1, flex: { xs: "1 1 auto", md: "0 0 auto" }, alignItems: 'center', flexWrap: 'wrap' }}>
      <DatePicker
        label="Start Date"
        value={formData.startDate}
        onChange={(v) => setFormData({ ...formData, startDate: v })}
        minDate={new Date()}
        slotProps={{
          textField: {
            size: "small",
            required: true,
            sx: { width: 140, background: "#fff" }
          }
        }}
      />
      <DatePicker
        label="End Date"
        value={formData.endDate}
        onChange={(v) => setFormData({ ...formData, endDate: v })}
        minDate={formData.startDate || new Date()}
        slotProps={{
          textField: {
            size: "small",
            sx: { width: 140, background: "#fff" }
          }
        }}
      />
    </Box>
  </LocalizationProvider>

                  
                  <TextField
                  required
                    select
                    size="small"
                    label="Load Type"
                    value={formData.fullOrPartial}
                    onChange={(e) =>
                      setFormData({ ...formData, fullOrPartial: e.target.value })
                    }
                    sx={{ flex: "0 0 200px", background: "#fff" }}
                  >
                      {["Full", "Partial", "Full or Partial"].map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Feet"
                    type="number"
                    value={formData.length}
                    onChange={(e) => {
                                                          const val = e.target.value;
                                                          if (val === "" || /^\d{1,3}$/.test(val)) {
                                                            setFormData({ ...formData, length: val });
                                                          }
                                                        }}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                    sx={{ flex: "0 0 200px", background: "#fff" }}
                  />
                  <TextField
                    size="small"
                    label="Weight"
                    type="number"
                    value={formData.weight}
                    onChange={(e) => {
                                                          const val = e.target.value;
                                                          if (val === "" || /^\d{1,8}$/.test(val)) {
                                                            setFormData({ ...formData, weight: val });
                                                          }
                                                        }}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">lbs</InputAdornment> }}
                    sx={{ flex: "0 0 200px", background: "#fff" }}
                  />
                  
  <TextField
    size="small"
    label="Search Age"
    type="number"
    value={formData.searchAge}
    onChange={(e) => {
      let val = e.target.value;
      if (val === "") {
        setFormData({ ...formData, searchAge: "" });
      } else {
        val = Number(val);
        val = Math.max(0, Math.min(24, val));
        setFormData({ ...formData, searchAge: String(val) });
      }
    }}
    InputProps={{
      endAdornment: <InputAdornment position="end">hr</InputAdornment>,
    }}
    sx={{
      flex: "0 0 150px",
      width: 90,
      maxWidth: 120,
      background: "#fff",
    }}
  />
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    alignItems: 'center', 
                    ml: { xs: 0, md: 1 }, 
                    flexShrink: 0,
                    justifyContent: { xs: 'space-between', md: 'flex-start' }
                  }}>
                  <Tooltip title="Collapse Form">
                      <IconButton size="small" onClick={() => setFormOpen(false)} sx={{ color: 'primary.main', mr: 0.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                              <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                              <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5 }} />
                          </Box>
                      </IconButton>
                  </Tooltip>
  <ButtonGroup
    size="small"
    variant="contained"
    sx={{ flex: "0 0 auto", width: { xs: '100%', md: 'auto' } }}
  >
    <Button
      variant="contained"
      color="primary"
      startIcon={<SearchIcon />}
      onClick={() => handleSearch(false)}
      disabled={isSearchDisabled} 
    sx={{
      
      height: '35px', 
      minWidth: 90,
      width: { xs: '100%', md: 'auto' },
      fontSize: '0.85rem', 
      fontWeight: 'bold',
      
      
      "& .MuiButton-startIcon": {
        marginRight: { xs: '8px', md: '-11px' }, 
        marginLeft: '0px',
      },
      
      padding: '6px 8px',

      
      backgroundColor: theme.palette.primary.main,
      "&:hover": { backgroundColor: theme.palette.primary.dark },
      
      
      ...(isSearchDisabled && {
        backgroundColor: "#bdbdbd !important",
        color: "#424242 !important",
        cursor: "not-allowed",
      }),
    }}
  >
      Search
    </Button>

  </ButtonGroup>

                  <IconButton onClick={handleMenuClick}>
                    <MoreVertIcon />
                  </IconButton>
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem
                      onClick={() => {
                        handleClear();
                        handleMenuClose();
                      }}
                    >
                      Clear Filters
                    </MenuItem>
                    
                  </Menu>
                </Box>
              </Box>
              </Collapse>
            </StyledPaper>

  {searchResults.length > 0 && (
      <>
          <Box
              sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  backgroundColor: "#f0f0f0",
                  borderTop: "1px solid #ddd",
                  borderBottom: "1px solid #ddd",
              }}
          >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
    onClick={() => {
      // Only refresh if we have previously searched with valid criteria
      if (lastSearchedFormData) {
        handleSearch(true); // Use background refresh with last searched data
      }
    }}
    size="small"
    color="primary"
    title="Refresh Search"
    disabled={!lastSearchedFormData} // Disable if no search has been performed yet
  >
    <RefreshIcon />
  </IconButton>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {searchResults.length} Results
                  </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                      value={currentSortOptionLabel}
                      onChange={(e) => {
                          const selected = SortSelectOptions.find(
                              (opt) => opt.label === e.target.value
                          );
                          if (selected) setSortConfig(selected);
                      }}
                      sx={{ backgroundColor: "#fff", fontSize: "0.8rem" }}
                  >
                      {SortSelectOptions.map((option) => (
                          <MenuItem key={option.label} value={option.label}>
                              {option.label}
                          </MenuItem>
                      ))}
                  </Select>
              </FormControl>
          </Box>

          <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            maxHeight: "calc(100vh - 300px)", 
          }}
        >
            <TableContainer
    component={Paper}
    sx={{
      maxHeight: "100%",
      overflowX: "auto",
      overflowY: "auto",
    }}
  >
    <Table
      size="small"
      sx={{
        // On mobile: use fixed min-width so columns don't squish
        // On desktop: keep full-width fixed layout as before
        width: "100%",
        minWidth: { xs: 780, sm: "100%" },
        tableLayout: "fixed",
        "& .MuiTableCell-root": {
          padding: "7px 11px",
          fontSize: "0.7rem",
          lineHeight: 1.2,
        },
      }}
    >       <TableHead
                          sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                              backgroundColor: "#11224d"
                          }}
                      >
                          <TableRow>
                            
                              {headCells.map((headCell, index) => {
                                  const tooltipText = 
                                      headCell.id === 'DH-O' ? 'Deadhead to Origin' : 
                                      headCell.id === 'DH-D' ? 'Deadhead from Destination' : 
                                      null;
                                  const cellContent = headCell.sortable ? (
                                      <TableSortLabel
                                          active={sortConfig.key === headCell.id}
                                          direction={
                                              sortConfig.key === headCell.id
                                                  ? sortConfig.order
                                                  : "asc"
                                          }
                                          onClick={() => handleRequestSort(headCell.id)}
                                          IconComponent={CustomSortArrow}
                                          sx={{
                                              "&.Mui-active": {
                                                  color: "white",
                                              },
                                          }}
                                      >
                                          {headCell.label}
                                      </TableSortLabel>
                                  ) : (
                                      headCell.label
                                  );
                                  return (
                                      <TableCell
                                          key={headCell.id}
                                          align="center"
                                          sortDirection={
                                              sortConfig.key === headCell.id ? sortConfig.order : false
                                          }
                                          sx={{
                                              backgroundColor: "#11224d",
                                              color: "white", 
                                            '&:hover': {
                                                  color: 'white', 
              
                                          '& .MuiTableSortLabel-root': {
                                            color: 'white !important', 
                                              },
                                                  },

                                              fontSize: "0.7rem",
                                              fontWeight: "bold",
                                              padding: "6px 10px",
                                              whiteSpace: "nowrap",
                                              width: columnWidths[headCell.label],
                                              verticalAlign: "middle",
                                              borderBottom: 'none',
                                          }}
                                      >
                                          {tooltipText ? (
                                              <Tooltip title={tooltipText} enterDelay={300}>
                                                  <span>{cellContent}</span>
                                              </Tooltip>
                                          ) : (
                                              cellContent
                                          )}
                                          {index === 0 && <TableProgressBar sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', zIndex: 5 }} />}
                                      </TableCell>
                                  );
                              })}
                          </TableRow>
                      </TableHead>

                      <TableBody>
                          {sortedResults.map((truck, index) => (
                              <React.Fragment key={`${truck.id}-${index}`}>
                                  <Row
                                      truck={truck}
                                      equipmentTypes={equipmentTypes}
                                      destinationType={formData.destination ? 'cityState' : null}
                                      onOpenDealDesk={(truck) => {
                                        onOpenDealDesk({ ...truck, _itemType: 'truck' });
                                      }}
                                  />
                                  {index === sortedResults.length - 1 && (
                                      <TableRow>
                                          <TableCell ref={lastTruckElementRef} colSpan={13} sx={{ py: 2, textAlign: 'center', border: 'none' }}>
                                              {loadingMore && <CircularProgress size={24} />}
                                              {!hasMore && searchResults.length > 0 && (
                                                  <Typography variant="caption" color="text.secondary">
                                                      End of results
                                                  </Typography>
                                              )}
                                          </TableCell>
                                      </TableRow>
                                  )}
                              </React.Fragment>
                          ))}
                      </TableBody>
                  </Table>
              </TableContainer>
          </Box>
      </>
  )}


            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={() => setSnackbarOpen(false)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert severity="success" sx={{ width: "100%" }}>
                {searchResults.length} trucks found
              </Alert>
            </Snackbar>

          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  export default SearchTrucks;